import React, { useState, useEffect } from 'react';
import { Container, Navbar, Nav, Badge } from 'react-bootstrap';
import APIHandler from '../utils/APIHandler';
import { useNavigate } from 'react-router-dom';

function AdminPage() {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [newEmail, setNewEmail] = useState('');
    const [newRole, setNewRole] = useState('user');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [user, setUser] = useState(null);

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) {
            const u = JSON.parse(userData);
            setUser(u);
            if (u.role !== 'admin' && u.role !== 'superadmin') {
                navigate('/dashboard');
            }
        }
        loadUsers();
    }, [navigate]);

    const loadUsers = async () => {
        try {
            const resp = await APIHandler.makeRequest('get', '/auth/users');
            if (resp.Success) setUsers(resp.users || []);
        } catch (e) { console.error(e); }
    };

    const addUser = async (e) => {
        e.preventDefault();
        if (!newEmail.trim()) return;
        setLoading(true);
        setMessage('');
        try {
            const resp = await APIHandler.makeRequest('post', '/auth/users', { email: newEmail.trim().toLowerCase(), role: newRole });
            setMessage(resp.Message || (resp.Success ? 'User added' : 'Failed'));
            if (resp.Success) { setNewEmail(''); loadUsers(); }
        } catch (e) { setMessage('Error: ' + e.message); }
        setLoading(false);
    };

    const updateRole = async (email, role) => {
        try {
            const resp = await APIHandler.makeRequest('put', `/auth/users/${email}/role`, { role });
            if (resp.Success) loadUsers();
            else alert(resp.Message);
        } catch (e) { alert(e.message); }
    };

    const removeUser = async (email) => {
        if (!window.confirm(`Remove ${email} from the system?`)) return;
        try {
            const resp = await APIHandler.makeRequest('delete', `/auth/users/${email}`);
            if (resp.Success) loadUsers();
            else alert(resp.Message);
        } catch (e) { alert(e.message); }
    };

    const isSuperadmin = user?.role === 'superadmin';

    const roleBadge = (role) => {
        const colors = { superadmin: 'danger', admin: 'warning', user: 'info' };
        return <Badge bg={colors[role] || 'secondary'}>{role}</Badge>;
    };

    return (
        <div style={styles.page}>
            <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet" />
            <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />

            <Navbar style={styles.navbar}>
                <Container fluid>
                    <Navbar.Brand style={{ color: '#f1f5f9', fontSize: 16, fontWeight: 600 }}>
                        <span className="material-icons" style={{ fontSize: 20, marginRight: 8, verticalAlign: 'middle' }}>admin_panel_settings</span>
                        User Management
                    </Navbar.Brand>
                    <Nav>
                        <Nav.Link onClick={() => navigate('/dashboard')} style={{ color: '#94a3b8' }}>
                            <span className="material-icons" style={{ fontSize: 18 }}>chat</span> Chat
                        </Nav.Link>
                    </Nav>
                </Container>
            </Navbar>

            <div style={styles.content}>
                {/* Add user form */}
                <div style={styles.card}>
                    <h3 style={styles.cardTitle}>Add User</h3>
                    <form onSubmit={addUser} style={styles.form}>
                        <input
                            type="email"
                            placeholder="email@lecfl.com"
                            value={newEmail}
                            onChange={e => setNewEmail(e.target.value)}
                            style={styles.input}
                            required
                        />
                        <select value={newRole} onChange={e => setNewRole(e.target.value)} style={styles.select}>
                            <option value="user">User</option>
                            {isSuperadmin && <option value="admin">Admin</option>}
                            {isSuperadmin && <option value="superadmin">Superadmin</option>}
                        </select>
                        <button type="submit" disabled={loading} style={styles.addBtn}>
                            <span className="material-icons" style={{ fontSize: 18 }}>person_add</span>
                            Add
                        </button>
                    </form>
                    {message && <div style={styles.message}>{message}</div>}
                </div>

                {/* Users list */}
                <div style={styles.card}>
                    <h3 style={styles.cardTitle}>Users ({users.length})</h3>
                    <div style={styles.table}>
                        <div style={styles.tableHeader}>
                            <span style={{ flex: 2 }}>Email</span>
                            <span style={{ flex: 1 }}>Name</span>
                            <span style={{ flex: 1 }}>Role</span>
                            <span style={{ flex: 1 }}>Last Login</span>
                            <span style={{ width: 120 }}>Actions</span>
                        </div>
                        {users.map(u => (
                            <div key={u.email} style={styles.tableRow}>
                                <span style={{ flex: 2, color: '#e2e8f0', fontSize: 13 }}>{u.email}</span>
                                <span style={{ flex: 1, color: '#94a3b8', fontSize: 13 }}>{u.name || '-'}</span>
                                <span style={{ flex: 1 }}>{roleBadge(u.role)}</span>
                                <span style={{ flex: 1, color: '#64748b', fontSize: 12 }}>
                                    {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}
                                </span>
                                <span style={{ width: 120, display: 'flex', gap: 4 }}>
                                    {isSuperadmin && u.role === 'user' && (
                                        <button onClick={() => updateRole(u.email, 'admin')} style={styles.actionBtn} title="Upgrade to Admin">
                                            <span className="material-icons" style={{ fontSize: 16 }}>arrow_upward</span>
                                        </button>
                                    )}
                                    {isSuperadmin && u.role === 'admin' && (
                                        <>
                                            <button onClick={() => updateRole(u.email, 'superadmin')} style={{ ...styles.actionBtn, color: '#f87171' }} title="Upgrade to Superadmin">
                                                <span className="material-icons" style={{ fontSize: 16 }}>star</span>
                                            </button>
                                            <button onClick={() => updateRole(u.email, 'user')} style={styles.actionBtn} title="Downgrade to User">
                                                <span className="material-icons" style={{ fontSize: 16 }}>arrow_downward</span>
                                            </button>
                                        </>
                                    )}
                                    {u.role !== 'superadmin' && (
                                        <button onClick={() => removeUser(u.email)} style={{ ...styles.actionBtn, color: '#ef4444' }} title="Remove">
                                            <span className="material-icons" style={{ fontSize: 16 }}>delete</span>
                                        </button>
                                    )}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Role legend */}
                <div style={styles.legend}>
                    {roleBadge('superadmin')} Can execute GAM commands, manage all users
                    &nbsp;&nbsp;{roleBadge('admin')} Can manage users, view all data
                    &nbsp;&nbsp;{roleBadge('user')} Can use chat, view data only
                </div>
            </div>
        </div>
    );
}

const styles = {
    page: { minHeight: '100vh', backgroundColor: '#0a0f1a', fontFamily: "'Inter', sans-serif", color: '#e2e8f0' },
    navbar: { borderBottom: '1px solid rgba(255,255,255,0.06)', backgroundColor: 'rgba(15,23,42,0.8)', padding: '8px 0' },
    content: { maxWidth: 900, margin: '0 auto', padding: '24px 16px' },
    card: { backgroundColor: '#151d2e', borderRadius: 12, padding: 24, marginBottom: 16, border: '1px solid rgba(255,255,255,0.06)' },
    cardTitle: { fontSize: 16, fontWeight: 600, marginBottom: 16, color: '#f1f5f9' },
    form: { display: 'flex', gap: 8, alignItems: 'center' },
    input: {
        flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)',
        backgroundColor: '#0a0f1a', color: '#e2e8f0', fontSize: 14, outline: 'none',
    },
    select: {
        padding: '10px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)',
        backgroundColor: '#0a0f1a', color: '#e2e8f0', fontSize: 14,
    },
    addBtn: {
        display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', borderRadius: 8,
        background: 'linear-gradient(135deg, #3b82f6, #2563eb)', border: 'none', color: 'white',
        fontSize: 13, fontWeight: 600, cursor: 'pointer',
    },
    message: { marginTop: 12, fontSize: 13, color: '#94a3b8' },
    table: { display: 'flex', flexDirection: 'column', gap: 2 },
    tableHeader: {
        display: 'flex', alignItems: 'center', padding: '8px 12px', fontSize: 11, color: '#64748b',
        textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid rgba(255,255,255,0.06)',
    },
    tableRow: {
        display: 'flex', alignItems: 'center', padding: '10px 12px', borderRadius: 8,
        transition: 'background 0.15s',
    },
    actionBtn: {
        background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', padding: 4,
        borderRadius: 6, display: 'flex',
    },
    legend: { textAlign: 'center', fontSize: 12, color: '#64748b', marginTop: 8 },
};

export default AdminPage;
